/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../../services/apiClient";
import { appointmentService } from "../../../services/appointmentService";
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

const AddAppoinments = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    patient_id: "",
    doctor_id: "",
    appointment_type_id: "",
    appointment_date: "",
    appointment_time: "",
    reason: "",
    notes: "",
  });

  // Data for selects
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  // Load booking selects independently — a doctors failure must not skip types.
  useEffect(() => {
    const loadData = async () => {
      const [doctorsResult, typesResult] = await Promise.allSettled([
        apiClient.get("/appointments/doctors"),
        apiClient.get("/appointments/types"),
      ]);

      if (doctorsResult.status === "fulfilled") {
        setDoctors(doctorsResult.value.data?.data || []);
      } else {
        console.error("Error loading doctors for booking:", doctorsResult.reason);
      }

      if (typesResult.status === "fulfilled") {
        setAppointmentTypes(typesResult.value.data?.data || []);
      } else {
        console.error("Error loading appointment types:", typesResult.reason);
      }
    };
    loadData();
  }, []);

  // Search patients with debounce
  useEffect(() => {
    if (formData.patient_id) return;
    if (patientSearch.length < 2) {
      setPatients([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/patients/search?term=${encodeURIComponent(patientSearch)}`);
        // JSON:API format: { data: [{ id, type, attributes: { ... } }] }
        const raw = res.data?.data || [];
        const parsed = raw.map((item) => {
          const attrs = item.attributes || item;
          return {
            id: Number(item.id || attrs.id),
            first_name: attrs['first-name'] || attrs.first_name || '',
            last_name: attrs['last-name'] || attrs.last_name || '',
            identification: attrs['identification'] || '',
            phone: attrs['phone'] || '',
            mobile: attrs['mobile'] || '',
          };
        });
        setPatients(parsed);
      } catch {
        setPatients([]);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [patientSearch, formData.patient_id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patient_id) newErrors.patient_id = "Seleccione un paciente";
    if (!formData.doctor_id) newErrors.doctor_id = "Seleccione un doctor";
    if (!formData.appointment_type_id) newErrors.appointment_type_id = "Seleccione el tipo de cita";
    if (!formData.appointment_date) newErrors.appointment_date = "Seleccione la fecha";
    if (!formData.appointment_time) newErrors.appointment_time = "Seleccione la hora";
    if (!formData.reason) newErrors.reason = "Ingrese el motivo de la cita";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccess(false);

    if (!validate()) return;

    setLoading(true);
    try {
      await appointmentService.create({
        patient_id: Number(formData.patient_id),
        doctor_id: Number(formData.doctor_id),
        appointment_type_id: Number(formData.appointment_type_id),
        scheduled_date: formData.appointment_date,
        scheduled_time: formData.appointment_time,
        reason: formData.reason,
        notes: formData.notes || undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate("/appointments"), 1500);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : "Error al crear la cita. Verifique los datos.";
      setSubmitError(typeof message === "string" ? message : "Error al crear la cita.");
    } finally {
      setLoading(false);
    }
  };

  const selectedPatient = patients.find(p => String(p.id) === String(formData.patient_id));

  // Get today's date for min date
  const today = new Date().toISOString().split("T")[0];

  return (
    <>
          {/* Page Header */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Registro de Cita</h3>
                <Breadcrumb items={[{ label: 'Citas', to: '/appointments' }, { label: 'Nueva Cita' }]} />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  {success && (
                    <div className="alert alert-success">
                      <i className="fa fa-check-circle me-2"></i>
                      Cita creada exitosamente. Redirigiendo...
                    </div>
                  )}
                  {submitError && (
                    <div className="alert alert-danger">
                      <i className="fa fa-exclamation-circle me-2"></i>
                      {submitError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    {/* Paciente */}
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Datos del Paciente</h4>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Buscar Paciente <span className="login-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Nombre, cédula o expediente..."
                            value={patientSearch}
                            onChange={(e) => setPatientSearch(e.target.value)}
                          />
                          {patients.length > 0 && !formData.patient_id && (
                            <div
                              className="list-group mt-1 shadow-sm"
                              style={{
                                position: "absolute",
                                zIndex: 1050,
                                width: "calc(100% - 24px)",
                                maxHeight: 200,
                                overflowY: "auto",
                              }}
                            >
                              {patients.map((p) => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="list-group-item list-group-item-action py-2"
                                  onClick={() => {
                                    handleChange("patient_id", p.id);
                                    setPatientSearch(`${p.first_name} ${p.last_name}`);
                                  }}
                                >
                                  <strong>
                                    {p.first_name} {p.last_name}
                                  </strong>
                                  {p.identification && (
                                    <span className="text-muted ms-2">
                                      — {p.identification}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          {errors.patient_id && (
                            <small className="text-danger">{errors.patient_id}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        {selectedPatient && (
                          <div className="card bg-light mt-1">
                            <div className="card-body py-2">
                              <strong>
                                {selectedPatient.first_name} {selectedPatient.last_name}
                              </strong>
                              <br />
                              <small className="text-muted">
                                {selectedPatient.identification || "Sin cédula"} •{" "}
                                {selectedPatient.phone || selectedPatient.mobile || "Sin teléfono"}
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger float-end"
                                onClick={() => {
                                  handleChange("patient_id", "");
                                  setPatientSearch("");
                                }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Detalles de la cita */}
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Detalles de la Cita</h4>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-4">
                        <div className="form-group local-forms">
                          <label>
                            Doctor <span className="login-danger">*</span>
                          </label>
                          <select
                            className={`form-control ${errors.doctor_id ? "is-invalid" : ""}`}
                            value={formData.doctor_id}
                            onChange={(e) => handleChange("doctor_id", e.target.value)}
                          >
                            <option value="">Seleccione un doctor</option>
                            {doctors.map((d) => (
                              <option key={d.id} value={d.id}>
                                Dr. {d.first_name} {d.last_name}
                                {d.specialty ? ` — ${d.specialty.name}` : ""}
                              </option>
                            ))}
                          </select>
                          {errors.doctor_id && (
                            <small className="text-danger">{errors.doctor_id}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-4">
                        <div className="form-group local-forms">
                          <label>
                            Tipo de Cita <span className="login-danger">*</span>
                          </label>
                          <select
                            className={`form-control ${errors.appointment_type_id ? "is-invalid" : ""}`}
                            value={formData.appointment_type_id}
                            onChange={(e) => handleChange("appointment_type_id", e.target.value)}
                          >
                            <option value="">Seleccione tipo</option>
                            {appointmentTypes.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.duration_minutes} min)
                              </option>
                            ))}
                          </select>
                          {errors.appointment_type_id && (
                            <small className="text-danger">{errors.appointment_type_id}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-4">
                        <div className="form-group local-forms">
                          <label>
                            Fecha <span className="login-danger">*</span>
                          </label>
                          <input
                            className={`form-control ${errors.appointment_date ? "is-invalid" : ""}`}
                            type="date"
                            min={today}
                            value={formData.appointment_date}
                            onChange={(e) => handleChange("appointment_date", e.target.value)}
                          />
                          {errors.appointment_date && (
                            <small className="text-danger">{errors.appointment_date}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-4">
                        <div className="form-group local-forms">
                          <label>
                            Hora <span className="login-danger">*</span>
                          </label>
                          <input
                            className={`form-control ${errors.appointment_time ? "is-invalid" : ""}`}
                            type="time"
                            value={formData.appointment_time}
                            onChange={(e) => handleChange("appointment_time", e.target.value)}
                          />
                          {errors.appointment_time && (
                            <small className="text-danger">{errors.appointment_time}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-8">
                        <div className="form-group local-forms">
                          <label>
                            Motivo <span className="login-danger">*</span>
                          </label>
                          <input
                            className={`form-control ${errors.reason ? "is-invalid" : ""}`}
                            type="text"
                            placeholder="Motivo de la cita"
                            value={formData.reason}
                            onChange={(e) => handleChange("reason", e.target.value)}
                          />
                          {errors.reason && (
                            <small className="text-danger">{errors.reason}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group local-forms">
                          <label>Notas adicionales</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Notas u observaciones..."
                            value={formData.notes}
                            onChange={(e) => handleChange("notes", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="doctor-submit text-end">
                          <button
                            type="submit"
                            className="btn btn-primary submit-form me-2"
                            disabled={loading}
                          >
                            {loading ? (
                              <Spinner size="sm" text="Guardando..." />
                            ) : (
                              <>
                                <i className="fa fa-check me-1"></i> Guardar Cita
                              </>
                            )}
                          </button>
                          <Link to="/appointments" className="btn btn-secondary cancel-form">
                            <i className="fa fa-times me-1"></i> Cancelar
                          </Link>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
    </>
  );
};

export default AddAppoinments;
